import { formatWholeNumberWithGrouping } from "@/utils/numberFormatting";
import { Product } from "@generated/types";

export const USD_STABLECOINS_REGEX = /USD/i;
export const DIGITAL_ASSETS_DISPLAY_DECIMALS = 6;
export const CURRENCIES_EXCHANGE_RATE_DECIMALS = 6;
export const DIGITAL_ASSETS_EXCHANGE_RATE_DECIMALS = 8;

const roundToFixed = (num: number, decimals: number) => {
  const factor = Math.pow(10, decimals);
  return (Math.round(num * factor) / factor).toFixed(decimals);
};

/**
 * Calculate the effective decimal places for USD stablecoins.
 * Returns minimum 2 decimals, but extends to show all significant digits
 * (no trailing zeros after the 2nd decimal place).
 *
 * Examples:
 * - getStablecoinDecimals(1.330009, 6) → 6 (shows "1.330009")
 * - getStablecoinDecimals(1.300000, 6) → 2 (shows "1.30")
 * - getStablecoinDecimals(1.3, 6) → 2 (shows "1.30")
 */
export const getStablecoinDecimals = (
  amount: number,
  maxDecimals: number
): number => {
  const MIN_DECIMALS = 2;

  // Handle edge cases
  if (amount === 0 || maxDecimals <= MIN_DECIMALS) {
    return MIN_DECIMALS;
  }

  // Convert to string with full precision to find significant digits
  const fullPrecision = amount.toFixed(maxDecimals);
  const [, decimalPart] = fullPrecision.split(".");

  if (!decimalPart) {
    return MIN_DECIMALS;
  }

  // Find the last non-zero digit position (1-indexed from left)
  let lastSignificantIndex = MIN_DECIMALS;
  for (let i = decimalPart.length - 1; i >= MIN_DECIMALS; i--) {
    if (decimalPart[i] !== "0") {
      lastSignificantIndex = i + 1; // +1 because we want count, not index
      break;
    }
  }

  return Math.max(MIN_DECIMALS, lastSignificantIndex);
};

/**
 * Decimal context types for currency formatting
 */
export type DecimalContext = "display" | "exchange_rate" | "input";

export const formatCurrency = (
  amount: number,
  currency: string,
  products: Product[],
  numberOnly = false,
  cryptoDisplayDecimals = DIGITAL_ASSETS_DISPLAY_DECIMALS,
  exchangeRate = false
) => {
  if (!currency || !products) {
    return undefined;
  }

  const product = products?.find((p) => p.code === currency);

  if (!product) {
    return numberOnly ? amount.toFixed(2) : `${amount.toFixed(2)} ${currency}`;
  }

  // Determine decimal places inline based on context
  let decimals: number;
  const isDigitalAsset = product.decimalPlaces && product.decimalPlaces > 2;
  const isUsdStablecoin = USD_STABLECOINS_REGEX.test(currency);

  if (exchangeRate) {
    // Exchange rate context: high precision
    if (isDigitalAsset && !isUsdStablecoin) {
      decimals = DIGITAL_ASSETS_EXCHANGE_RATE_DECIMALS; // 8 decimals for digital assets
    } else {
      decimals = CURRENCIES_EXCHANGE_RATE_DECIMALS; // 6 decimals for currencies and stablecoins
    }
  } else {
    // Display context
    if (isUsdStablecoin) {
      // Use dynamic decimals: minimum 2, but show all significant digits
      decimals = getStablecoinDecimals(amount, product.decimalPlaces);
    } else if (isDigitalAsset) {
      decimals = cryptoDisplayDecimals; // Default 5 for digital assets
    } else {
      decimals = product.decimalPlaces; // Use product's decimal places for currencies
    }
  }

  const formattedAmount = roundToFixed(amount, decimals);

  // Add comma formatting to the amount
  const [wholePart = "0", decimalPart] = formattedAmount.split(".");
  const formattedWholePart = formatWholeNumberWithGrouping(wholePart);
  const finalFormattedAmount = decimalPart
    ? `${formattedWholePart}.${decimalPart}`
    : formattedWholePart;

  return numberOnly
    ? finalFormattedAmount
    : `${finalFormattedAmount} ${currency}`;
};

export default formatCurrency;
